import { useCallback, useEffect, useState } from "react";
import api from "../api/axios";
import "../styles/report.css";
import "../styles/global.css";
import Headers from "../components/Header";
import { signMultipleS3Urls } from "../utils/s3Helper";
import { useAuth } from '../context/AuthContext';

export default function Report() {
    const { user } = useAuth();
    const [data, setData] = useState([]); // filtered data
    // const [allData, setAllData] = useState([]); // original data from API
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    // const [filterDate, setFilterDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Filtros
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // YYYY-MM-DD
    const [startTime, setStartTime] = useState("00:00:00");
    const [endTime, setEndTime] = useState("23:59:00");
    const [status, setStatus] = useState("");
    const [verificationType, setVerificationType] = useState("all"); // all, MISSING_PPE, PROHIBITED_ITEM

    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const isFilterApplied = searchTerm || selectedDate || startTime || endTime || status;

    // Estadísticas
    const [stats, setStats] = useState({
        totalVerified: 0,
        totalWithIncidents: 0
    });

    const rowsPerPage = 8;
    // const defaultDate = new Date().toISOString().split("T")[0];

    const timeSlots = Array.from({ length: 48 }, (_, index) => {
        const hours = String(Math.floor(index / 2)).padStart(2, "0");
        const minutes = index % 2 === 0 ? "00" : "30";
        return { key: `${hours}:${minutes} hrs`, value: `${hours}:${minutes}:00` };
    });

    // ✅ Must be defined before useMemo
    // const getRandomDate = (start, end) => {
    //     const date = new Date(
    //         start.getTime() + Math.random() * (end.getTime() - start.getTime())
    //     );
    //     return date.toISOString().split("T")[0];
    // };

    const openModal = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedItem(null);
    };

    // ✅ Must be defined before useEffect
    // const mockData = useMemo(() => {
    //     return Array.from({ length: 42 }, (_, i) => ({
    //         id: i + 1,
    //         date: getRandomDate(
    //             new Date("2026-02-01"),
    //             new Date("2026-02-10")
    //         ),
    //         time: `${String(i % 12).padStart(2, "0")}:${String(i % 60).padStart(2, "0")}`,
    //         img: i % 3 === 0 ? "./user1.png" : "./defaultUser.jpg",
    //         status: i % 2 === 0 ? "Aprobado" : "Rechazados"
    //     }));
    // }, []);


    // Cargar violations desde el API
    const fetchViolations = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const params = new URLSearchParams({
                date: selectedDate,
                startTime: startTime,
                endTime: endTime,
            });

            // Agregar filtro de tipo si no es "all"
            if (verificationType !== "all") {
                params.append("violation_type", verificationType);
            }

            const response = await api.get(`/violations?${params.toString()}`);

            if (response.data.success) {
                const violations = response.data.violations || [];

                // Transformar violations al formato esperado por la UI
                const transformedData = violations.map((violation, index) => ({
                    id: violation.track_id || index,
                    violation_id: violation.violation_id,
                    time: formatTimestamp(violation.timestamp),
                    timestamp: violation.timestamp,
                    img: violation.image_s3_uris?.[0] || "./defaultUser.jpg",
                    s3Uri: violation.image_s3_uris?.[0], // Guardar S3 URI original
                    violation_type: violation.violation_type,
                    missing_ppe: violation.missing_ppe || [],
                    prohibited_items: violation.prohibited_items || [],
                    image_count: violation.image_count || 0,
                    completed: violation.completed
                }));

                // Extraer todas las URLs S3 para firmar
                const s3Uris = transformedData
                    .map(item => item.s3Uri)
                    .filter(uri => uri && uri.startsWith('s3://'));

                console.log(`Signing ${s3Uris.length} S3 URLs...`);

                // Firmar todas las URLs en paralelo (client-side)
                const signedUrlsMap = await signMultipleS3Urls(s3Uris, 3600); // 1 hora de expiración

                // Reemplazar URLs S3 con URLs firmadas
                const dataWithSignedUrls = transformedData.map(item => ({
                    ...item,
                    img: signedUrlsMap[item.s3Uri] || item.img || "./defaultUser.jpg"
                }));

                // setAllData(dataWithSignedUrls);
                setData(dataWithSignedUrls);

                // Actualizar estadísticas
                const uniqueTracks = new Set(violations.map(v => v.track_id)).size;
                setStats({
                    totalVerified: uniqueTracks,
                    totalWithIncidents: violations.length
                });

            } else {
                setError("No se pudieron cargar los eventos");
            }
        } catch (err) {
            console.error("Error fetching violations:", err);
            setError(err.response?.data?.error || "Error al cargar los eventos. Por favor intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    }, [selectedDate, startTime, endTime, verificationType]);

    // Formatear timestamp a formato legible
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString('es-CL', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return timestamp;
        }
    };

    // ================= EFFECTS =================
    // Fetch from API
    useEffect(() => {
        fetchViolations();
        setPage(1);
    }, [fetchViolations, selectedDate, startTime, endTime, verificationType]);


    // Cargar datos al montar el componente
    // useEffect(() => {
    //     let result = [...allData];

    //     if (searchTerm) {
    //         result = result.filter(item =>
    //             item.id.toString().includes(searchTerm)
    //         );
    //     }

    //     if (filterDate) {
    //         result = result.filter(item => item.date === filterDate);
    //     }

    //     if (startTime && endTime) {
    //         result = result.filter(
    //             item => item.time >= startTime && item.time <= endTime
    //         );
    //     }

    //     if (status) {
    //         result = result.filter(item => item.status === status);
    //         setVerificationType(status);
    //     }

    //     setData(result);
    //     setPage(1);
    // }, [allData, searchTerm, filterDate, startTime, endTime, status]);

    const totalPages = Math.ceil(data.length / rowsPerPage);
    const start = (page - 1) * rowsPerPage;
    const visibleRows = data
        .filter(item =>
            searchTerm
                ? item.id.toString().includes(searchTerm)
                : true
        )
        .slice(start, start + rowsPerPage);
    const clearFilters = () => {
        setSearchTerm("");
        setSelectedDate("");
        setStartTime("00:00:00");
        setEndTime("23:59:00");
        setStatus("");
    };

    return (
        <>
            <Headers />
            <div className="verification-container">
                <div className="left-section">
                    <div className="col-12 welcome-text ">Bienvenido</div>
                    <div className="col-12 person-name-text">{user.name}</div>
                </div>
            </div>

            {/* Left Section */}
            <div className="verification-container">
                <div className="left-section">
                    <div className="field-group field-group-noflex-row">
                        <div className="col-4 field-group">
                            <label>Selección Totem de Verificación</label>
                            <select>
                                <option>TOTEM 01 - ACCESO NORTE</option>
                            </select>
                        </div>
                        <div className="col-3 search-box">
                            <label>Buscar por Track ID</label>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Ingresa el Track ID"
                            />
                        </div>
                    </div>

                    <div className="field-group">
                        <div className="date-time-group">
                            <div className="field-group">
                                <label>Fecha</label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </div>

                            <div className="field-group">
                                <label>Hora Inicio</label>
                                <select
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                >
                                    {timeSlots.map((item, index) => (
                                        <option key={index} value={item.value}>
                                            {item.key}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="field-group">
                                <label>Hora Fin</label>
                                <select
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                >
                                    {timeSlots.map((item, index) => (
                                        <option key={index} value={item.value}>
                                            {item.key}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="field-group">
                                <label>Tipo de Verificación</label>
                                <select
                                    value={verificationType}
                                    onChange={(e) => setVerificationType(e.target.value)}
                                >
                                    <option value="all">Todos</option>
                                    <option value="MISSING_PPE">EPP Faltante</option>
                                    <option value="PROHIBITED_ITEM">Item Prohibido</option>
                                </select>
                            </div>

                            <div className="field-group">
                                {/* <label>&nbsp;</label> */}
                                <button
                                    type="button"
                                    className="btn btn-secondary margin-17rem
                                "
                                    onClick={clearFilters}
                                    disabled={!isFilterApplied} >
                                    {/* {loading ? 'Buscando...' : 'Buscar'} */}
                                    Limpiar filtros
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mostrar errores */}
                    {error && (
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#fee',
                            color: '#c33',
                            borderRadius: '6px',
                            marginTop: '10px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}
                </div>

                {/* Right Section - Stats */}
                <div className="right-section">
                    <div className="status-row">
                        <div className="status-card blue">
                            <span className="count">{stats.totalVerified}</span>
                            <span className="label">
                                Total Personas
                                <br />
                                Verificadas
                            </span>
                        </div>

                        <div className="status-card red">
                            <span className="count">{stats.totalWithIncidents}</span>
                            <span className="label">
                                Total Personas
                                <br />
                                con Incidencias
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Results Grid */}
            <div className="report-page">
                {loading ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        fontSize: '16px',
                        color: '#666'
                    }}>
                        Cargando eventos...
                    </div>
                ) : data.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        fontSize: '16px',
                        color: '#666'
                    }}>
                        No se encontraron eventos para los filtros seleccionados
                    </div>
                ) : (
                    <>
                        <div className="container-fluid">
                            <div className="row g-4">
                                {visibleRows.map((item, index) => (
                                    <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={index}>
                                        <div className="card shadow-sm rounded-3 h-100">
                                            <div className="d-flex col-12">
                                                {/* Avatar/Image */}
                                                {item.img ? (
                                                    <img
                                                        src={item.img}
                                                        alt="evidence"
                                                        className="rounded-3 mt-2"
                                                        width="72"
                                                        height="72"
                                                        onError={(e) => {
                                                            e.target.src = "./defaultUser.jpg";
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="d-flex align-items-center justify-content-center bg-light rounded-3"
                                                        style={{ width: 72, height: 72 }}
                                                    >
                                                        <span className="fs-2 text-secondary">👤</span>
                                                    </div>
                                                )}

                                                {/* Content */}
                                                <div className="ms-3">
                                                    <div className="col-12 fw-bold fs-6">Track ID: {item.id}</div>
                                                    <div className="col-12 text-right" style={{ fontSize: '14px' }}>{item.time}</div>
                                                    <div
                                                        className="small"
                                                        style={{
                                                            color: item.violation_type === 'MISSING_PPE' ? '#dc3545' : '#ffc107',
                                                            fontWeight: '600'
                                                        }}
                                                    >
                                                        {item.violation_type === 'MISSING_PPE' ? 'EPP Faltante' : 'Item Prohibido'}
                                                    </div>
                                                    {/* {item.missing_ppe?.length > 0 && (
                                                        <div className="small">
                                                            Falta: {item.missing_ppe.join(', ')}
                                                        </div>
                                                    )}
                                                    {item.prohibited_items?.length > 0 && (
                                                        <div className="small">
                                                            Prohibido: {item.prohibited_items.join(', ')}
                                                        </div>
                                                    )} */}
                                                    <span
                                                        className="more-details text-primary text-decoration-underline"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => openModal(item)}
                                                    >
                                                        VER DETALLE
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Pagination */}
                        <div className="pagination">
                            <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                                Anterior
                            </button>

                            <span>
                                Página {page} de {totalPages}
                            </span>

                            <button
                                disabled={page === totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                Siguiente
                            </button>
                        </div>
                    </>
                )}
            </div>
            {showModal && selectedItem && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <div className="modal-header" style={{ borderBottom: '1px solid #e0dfdf', marginBottom: '10px' }}>
                            <div className="col-11 welcome-text">Detalle de Verificación</div>
                            <button className="close-btn" onClick={closeModal}>×</button>
                        </div>

                        <div className="modal-body">
                            <div className="container d-flex">
                                <div className="col-8 p-4" style={{borderRight: '1px solid #e0dfdf'}}>
                                    <img
                                        src={selectedItem.img}
                                        alt="user"
                                        width="100%"
                                        height="100%"
                                        className="rounded-3 mb-3"
                                    />
                                </div>

                                <div className="col-4 mt-5" style={{ paddingLeft: '20px'}}>
                                    <p><strong>ID:</strong> {selectedItem.id}</p>
                                    {selectedItem.missing_ppe?.length > 0 && (
                                        <p>
                                            <strong>Falta:</strong> {selectedItem.missing_ppe.join(', ')}
                                        </p>
                                    )}
                                    {selectedItem.prohibited_selectedItems?.length > 0 && (
                                        <p>
                                            <strong>Prohibido:</strong> {selectedItem.prohibited_items.join(', ')}
                                        </p>
                                    )}
                                    <p><strong>Fecha:</strong> {selectedItem.timestamp}</p>
                                    <p><strong>Hora:</strong> {selectedItem.time}</p>
                                    <p>
                                        <strong>Estado:</strong>{" "}
                                        <span
                                            className={
                                                selectedItem.status === "Aprobado" ? "text-success" : "text-danger"
                                            }
                                        >
                                            {selectedItem.violation_type === 'MISSING_PPE' ? 'EPP Faltante' : 'Item Prohibido' }
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={closeModal}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
